// src/hooks/useInvestigation.js
import { useState, useCallback, useRef } from 'react';
import { apiService } from '../services/api';

// Direction-agnostic edge key: source|target (sorted). No type — one edge per pair.
const edgeKey = e => [...[e.source, e.target].sort()].join('|');

export const DIMENSIONS_CONFIG = [
  { name: 'certificate',    label: 'Certificate',    color: '#3b82f6' },
  { name: 'icon_hash',      label: 'Icon Hash',      color: '#f97316' },
  { name: 'permission_set', label: 'Permission Set', color: '#22c55e' },
  { name: 'malware_family', label: 'Malware Family', color: '#ef4444' },
  { name: 'service',        label: 'Service',        color: '#a855f7' },
  { name: 'receiver',       label: 'Receiver',       color: '#ec4899' },
  { name: 'intent_action',  label: 'Intent Action',  color: '#facc15' },
  { name: 'artifact',       label: 'Artifact',       color: '#14b8a6' },
  { name: 'payload',        label: 'Shared Payload', color: '#FF003C' },
];

export const DIM_COLOR = Object.fromEntries(DIMENSIONS_CONFIG.map(d => [d.name, d.color]));

export const EXPANSION_BADGE_COLORS = [
  '#60a5fa', '#34d399', '#f472b6', '#fb923c', '#c084fc', '#fbbf24',
];

export function useInvestigation() {
  const [graphData, setGraphData]             = useState(null);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState(null);
  const [selectedNode, setSelectedNode]       = useState(null);
  const [hiddenEdgeTypes, setHiddenEdgeTypes] = useState(new Set());
  const [expandedNodes, setExpandedNodes]     = useState(new Set());

  // Generation tracking — refs so D3 closures read fresh values without re-render
  const nodeGenerationRef    = useRef(new Map()); // id → step int
  const generationCounterRef = useRef(0);
  const expansionSourceRef   = useRef(new Map()); // step → { id, name }

  // Mirrors current nodes for building existing_node_ids on expand
  const rawNodesRef = useRef([]);

  // Provenance map for collapse: id → { addedNodes: Set, addedEdgeKeys: Set }
  const expansionMapRef = useRef({});

  const buildGraph = useCallback(async (sha256, dimensions, limitPerPivot = 20, dateFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.buildGraph(sha256, dimensions, limitPerPivot, dateFilters);

      // Reset all generation tracking on fresh build
      nodeGenerationRef.current    = new Map();
      generationCounterRef.current = 0;
      expansionSourceRef.current   = new Map();
      expansionMapRef.current      = {};

      // Stamp every initial node at step 0
      (data.nodes || []).forEach(n => nodeGenerationRef.current.set(n.id, 0));
      rawNodesRef.current = data.nodes || [];

      setGraphData(data);
      setSelectedNode(null);
      setExpandedNodes(new Set([sha256]));
    } catch (err) {
      setError(err.message || 'Failed to build investigation graph');
    } finally {
      setLoading(false);
    }
  }, []);

  const expandNode = useCallback(async (sha256, dimensions, limitPerPivot = 20, dateFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const existingIds = rawNodesRef.current.map(n => n.id);
      const data = await apiService.expandGraphNode(sha256, existingIds, dimensions, limitPerPivot, dateFilters);

      // Increment step counter before setState so it's stable across potential double-invocations
      const step = generationCounterRef.current + 1;
      generationCounterRef.current = step;

      setGraphData(prev => {
        if (!prev) return data;

        const existingShas     = new Set(prev.nodes.map(n => n.id));
        const newNodes         = (data.nodes || []).filter(n => !existingShas.has(n.id));
        const addedNodes       = new Set(newNodes.map(n => n.id));

        // Build edge map keyed by source|target pair
        const edgeMap = new Map(prev.edges.map(e => [edgeKey(e), { ...e, dimensions: e.dimensions.map(d => ({ ...d, shared_values: [...d.shared_values] })) }]));
        const addedEdgeKeys = new Set();

        (data.edges || []).forEach(e => {
          const k = edgeKey(e);
          if (edgeMap.has(k)) {
            // Merge dimensions into existing edge
            const existing = edgeMap.get(k);
            e.dimensions.forEach(newDim => {
              const existingDim = existing.dimensions.find(d => d.type === newDim.type);
              if (existingDim) {
                existingDim.count = Math.max(existingDim.count, newDim.count);
                (newDim.shared_values || []).forEach(v => {
                  if (!existingDim.shared_values.includes(v)) existingDim.shared_values.push(v);
                });
              } else {
                existing.dimensions.push({ ...newDim, shared_values: [...(newDim.shared_values || [])] });
              }
            });
            existing.total_shared_dimensions = existing.dimensions.length;
            existing.total_shared_values = existing.dimensions.reduce((s, d) => s + d.count, 0);
          } else {
            edgeMap.set(k, { ...e, dimensions: e.dimensions.map(d => ({ ...d, shared_values: [...(d.shared_values || [])] })) });
            addedEdgeKeys.add(k);
          }
        });

        // Stamp new nodes with this step
        newNodes.forEach(n => nodeGenerationRef.current.set(n.id, step));

        // Record which node triggered this step
        const expandedNodeObj = prev.nodes.find(n => n.id === sha256);
        expansionSourceRef.current.set(step, {
          id:   sha256,
          name: expandedNodeObj?.app_name || sha256.slice(0, 12) + '…',
        });

        // Record provenance for collapse
        expansionMapRef.current[sha256] = { addedNodes, addedEdgeKeys };

        const mergedNodes = [...prev.nodes, ...newNodes];
        const mergedEdges = [...edgeMap.values()];
        rawNodesRef.current = mergedNodes;

        return { ...prev, nodes: mergedNodes, edges: mergedEdges };
      });

      setExpandedNodes(prev => new Set([...prev, sha256]));
    } catch (err) {
      setError(err.message || 'Failed to expand node');
    } finally {
      setLoading(false);
    }
  }, []);

  const collapseNode = useCallback((sha256) => {
    const em = expansionMapRef.current;

    const nodesToRemove    = new Set();
    const edgeKeysToRemove = new Set();
    const expandedToRemove = new Set();

    const queue   = [sha256];
    const visited = new Set();

    while (queue.length) {
      const sha = queue.shift();
      if (visited.has(sha)) continue;
      visited.add(sha);
      expandedToRemove.add(sha);

      const exp = em[sha];
      if (!exp) continue;

      exp.addedEdgeKeys.forEach(k => edgeKeysToRemove.add(k));
      exp.addedNodes.forEach(n => {
        nodesToRemove.add(n);
        if (em[n]) queue.push(n);
      });
    }

    // Clean up generation stamps and provenance for removed nodes
    nodesToRemove.forEach(id => nodeGenerationRef.current.delete(id));
    expandedToRemove.forEach(s => delete expansionMapRef.current[s]);

    setGraphData(prev => {
      if (!prev) return prev;

      const newEdges = prev.edges.filter(e => !edgeKeysToRemove.has(edgeKey(e)));

      const seedId = prev.nodes.find(n => n.is_seed)?.id;
      const stillConnected = new Set([seedId].filter(Boolean));
      newEdges.forEach(e => { stillConnected.add(e.source); stillConnected.add(e.target); });

      const newNodes = prev.nodes.filter(
        n => !nodesToRemove.has(n.id) || stillConnected.has(n.id)
      );

      rawNodesRef.current = newNodes;
      return { ...prev, nodes: newNodes, edges: newEdges };
    });

    setExpandedNodes(prev => {
      const next = new Set(prev);
      expandedToRemove.forEach(s => next.delete(s));
      return next;
    });
  }, []);

  const toggleEdgeType = useCallback((type) => {
    setHiddenEdgeTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setHiddenEdgeTypes(new Set());
  }, []);

  // Force-enable a list of dimension names — called after expand so newly-added
  // edges are not immediately hidden by the visibility filter.
  const forceEnableDims = useCallback((dimNames) => {
    setHiddenEdgeTypes(prev => {
      if (dimNames.every(n => !prev.has(n))) return prev;
      const next = new Set(prev);
      dimNames.forEach(n => next.delete(n));
      return next;
    });
  }, []);

  return {
    graphData, loading, error,
    selectedNode, setSelectedNode,
    hiddenEdgeTypes, toggleEdgeType, forceEnableDims,
    buildGraph, resetFilters,
    expandedNodes, expandNode, collapseNode,
    nodeGenerationRef, generationCounterRef, expansionSourceRef,
  };
}
